import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import AgentsEmptyState from 'components/@home/@settings/Tabs/Agents/AgentsEmptyState';
import AgentsList from 'components/@home/@settings/Tabs/Agents/AgentsList';
import CreateKnowledgeDialog from 'components/@home/@settings/Tabs/Agents/CreateKnowledgeDialog';
import ImportAgentModal from 'components/@home/@settings/Tabs/Agents/ImportAgentModal';
import AgentDeleteExclusivePanel from 'components/@home/@settings/Tabs/Agents/AgentDeleteExclusivePanel';
import NewAgentDialog from 'components/@home/@settings/Tabs/Agents/NewAgentDialog';
import ChatFlowFlowRoute from 'components/@home/@settings/Tabs/ChatFlowFlowRoute';
import RightPane from 'components/@home/@settings/Tabs/RightPane';
import connector from 'components/@home/@settings/Tabs/connector';
import DeleteDialog from 'components/common/DeleteDialog';
import DropzoneBox from 'components/common/DropzoneBox';
import Loading from 'components/common/Loading';
import orderBy from 'lodash/orderBy';
import ms from 'ms';
import { bool, node, object, objectOf } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import { chatFlowEntityTypeShape, chatFlowIntentShape, chatFlowShape } from 'shapes/chatFlowShapes';
import agenticSlug from 'utils/agenticSlug';
import agenticSectionPath, { agenticSectionName } from 'utils/agenticSectionPath';
import {
  buildAgenticPath,
  contextCurrentToRouteParts,
  parseAgenticRoute,
  routeToContextCurrent,
} from 'utils/agenticRoute';
import { hasCompanyFeature } from 'utils/companyFeatureFlags';
import AgentSkill from 'services/api/AgentSkill';
import AgentTool from 'services/api/AgentTool';
import Agent from 'services/api/Agent';
import { I18n, Translate } from 'utils/i18n';
import joinWithFinalAnd from 'utils/joinWithFinalAnd';
import toolNameConflictMessage from 'utils/toolNameConflict';
import useArray from 'utils/useArray';

const getStyles = theme => ({
  root: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  routeShell: {
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modal: {
    maxWidth: 500,
  },
  loading: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 320,
  },
});

const STANDALONE_PANE_TYPES = new Set(['flow', 'entity-type', 'intent', 'integration']);

const FieldLabel = ({ children, required = false }) => (
  <Typography
    component="label"
    variant="body2"
    sx={{ display: 'block', fontWeight: 600, mb: 0.75 }}
  >
    {required && (
      <Box component="span" sx={{ color: 'error.main', mr: 0.5 }}>
        *
      </Box>
    )}
    {children}
  </Typography>
);

FieldLabel.propTypes = {
  children: node.isRequired,
  required: bool,
};

const ChatFlowRedirect = ({ authCompany = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const canonical = agenticSectionName(authCompany);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/settings/agents') && canonical === 'chatFlow') {
      navigate(path.replace('/settings/agents', '/settings/chatFlow'), { replace: true });
    } else if (path.includes('/settings/chatFlow') && canonical === 'agents') {
      navigate(path.replace('/settings/chatFlow', '/settings/agents'), { replace: true });
    }
  }, [location.pathname, canonical, navigate]);

  return null;
};

ChatFlowRedirect.propTypes = {
  authCompany: object,
};

const ChatFlowSettings = ({
  actions,
  authCompany = null,
  chatFlows = {},
  chatFlowsLoaded = false,
  chatFlowIntents = {},
  chatFlowEntityTypes = {},
  chatFlowIntegrations = {},
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const navigate = useNavigate();
  const location = useLocation();

  const [numImports, setNumImports] = useState(0);
  const incNumImports = () => setNumImports(n => n + 1);
  const [toDelete, setToDelete] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deleteExclusive, setDeleteExclusive] = useState(false);
  const [stack, current, push, pop, clear, set] = useArray();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [newAgentOpen, setNewAgentOpen] = useState(false);
  const [createSkillOpen, setCreateSkillOpen] = useState(false);
  const [createAgentToolOpen, setCreateAgentToolOpen] = useState(false);
  const [createKnowledgeOpen, setCreateKnowledgeOpen] = useState(false);
  const [newSkillForm, setNewSkillForm] = useState({
    displayName: '',
    description: '',
  });
  const [newAgentToolForm, setNewAgentToolForm] = useState({
    name: '',
    description: '',
    kind: 'http',
  });
  const [createAgentToolError, setCreateAgentToolError] = useState('');

  const hasAgentic = hasCompanyFeature(authCompany?.featureFlags, 'agenticChatFlow');

  const agentDeleteTarget = useMemo(() => {
    if (!hasAgentic || !toDelete?._id || !chatFlows[toDelete._id]) {
      return null;
    }
    const flow = chatFlows[toDelete._id];
    if (flow.mode !== 'agent' || !flow.agent) {
      return null;
    }
    return flow;
  }, [hasAgentic, toDelete, chatFlows]);

  useEffect(() => {
    if (!agentDeleteTarget?.agent) {
      setDeletePreview(null);
      setDeleteExclusive(false);
      setDeletePreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    setDeletePreviewLoading(true);
    setDeleteExclusive(false);
    Agent.deletePreview(agentDeleteTarget.agent)
      .then(preview => {
        if (!cancelled) {
          setDeletePreview(preview);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDeletePreview(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDeletePreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agentDeleteTarget?.agent]);

  const route = useMemo(
    () => parseAgenticRoute(location.pathname, authCompany),
    [location.pathname, authCompany],
  );
  const selectedFlow = route.flowId;
  const setSelectedFlow = useCallback(
    flowId => {
      if (!flowId) navigate(agenticSectionPath(authCompany));
      else navigate(buildAgenticPath(authCompany, { flowId }));
    },
    [navigate, authCompany],
  );

  // Sync URL-derived entity (skill/tool/page/intent/...) into the in-memory
  // stack so existing consumers that read `context.current` keep working.
  useEffect(() => {
    const urlCurrent = routeToContextCurrent(route);
    const topIsUrlBacked =
      current &&
      ['skill', 'agent-tool', 'page', 'entity-type', 'intent', 'integration'].includes(
        current.type,
      );

    const matchesUrl =
      urlCurrent &&
      current &&
      current.type === urlCurrent.type &&
      (current.skill === urlCurrent.skill ||
        current.agentTool === urlCurrent.agentTool ||
        current.page === urlCurrent.page ||
        current.entityType === urlCurrent.entityType ||
        current.intent === urlCurrent.intent ||
        current.integration === urlCurrent.integration);

    if (urlCurrent && !matchesUrl) {
      set([urlCurrent]);
    } else if (!urlCurrent && topIsUrlBacked) {
      // URL has no entity selected but the stack still holds one → drop it.
      clear();
    }
    // We intentionally only sync from URL → stack here; the stack → URL
    // direction is handled by the wrapped `set/push/clear/pop` below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    route.flowId,
    route.section,
    route.skillId,
    route.toolId,
    route.pageId,
    route.entityTypeId,
    route.intentId,
    route.integrationId,
  ]);

  const closeImportModal = () => setImportModalOpen(false);
  const openImportModal = () => setImportModalOpen(true);

  useEffect(() => {
    actions.chatFlows.load();
    actions.chatFlowEntityTypes.load();
    actions.chatFlowIntents.load();
    actions.chatFlowIntegrations.load();
  }, [
    numImports,
    actions.chatFlowEntityTypes,
    actions.chatFlowIntents,
    actions.chatFlows,
    actions.chatFlowIntegrations,
  ]);

  useEffect(() => {
    actions.teams.load();
  }, [actions.teams]);

  useEffect(() => {
    if (!selectedFlow && stack.length > 0) {
      const paneType = current?.type;
      const keepFlowCreate = paneType === 'flow' && current?.flow === 'new';
      if (!keepFlowCreate) {
        clear();
      }
    }
  }, [selectedFlow, current?.type, current?.flow, stack.length, clear]);

  const navigateToContext = useCallback(
    item => {
      const parts = contextCurrentToRouteParts(item, route.section);
      navigate(
        buildAgenticPath(authCompany, {
          flowId: selectedFlow,
          ...parts,
        }),
      );
    },
    [navigate, authCompany, selectedFlow, route.section],
  );

  const isUrlBacked = item =>
    !!item &&
    ['skill', 'agent-tool', 'page', 'entity-type', 'intent', 'integration'].includes(item.type);

  const setContext = useCallback(
    item => {
      set([item]);
      if (isUrlBacked(item) && selectedFlow) navigateToContext(item);
    },
    [set, navigateToContext, selectedFlow],
  );

  const pushContext = useCallback(
    item => {
      push(item);
      if (isUrlBacked(item) && selectedFlow) navigateToContext(item);
    },
    [push, navigateToContext, selectedFlow],
  );

  const popContext = useCallback(() => {
    const top = stack[stack.length - 1];
    pop();
    if (isUrlBacked(top) && selectedFlow) {
      // After popping a URL-backed entity, navigate to its section root.
      const parts = contextCurrentToRouteParts(top);
      navigate(
        buildAgenticPath(authCompany, {
          flowId: selectedFlow,
          section: parts.section,
        }),
      );
    }
  }, [stack, pop, navigate, authCompany, selectedFlow]);

  const clearContext = useCallback(() => {
    clear();
    if (selectedFlow) {
      // Drop straight to the workspace's default section so we don't trigger
      // an intermediate redirect from `/flow` → `/flow/<default>`.
      const isAgentic = chatFlows?.[selectedFlow]?.mode === 'agent';
      navigate(
        buildAgenticPath(authCompany, {
          flowId: selectedFlow,
          section: isAgentic ? 'instructions' : 'pages',
          instructionKind: isAgentic ? 'base' : undefined,
        }),
      );
    }
  }, [clear, navigate, authCompany, selectedFlow, chatFlows]);

  const context = useMemo(
    () => ({
      stack,
      current,
      push: pushContext,
      pop: popContext,
      clear: clearContext,
      set: setContext,
      length: stack.length,
    }),
    [stack, current, pushContext, popContext, clearContext, setContext],
  );

  const onBackToList = useCallback(() => {
    clear();
    navigate(agenticSectionPath(authCompany));
  }, [clear, navigate, authCompany]);

  const editFlow =
    (flow = { _id: 'new', mode: 'pages' }) =>
    () => {
      if (flow._id !== 'new' && flow.mode === 'agent') {
        clear();
        navigate(
          buildAgenticPath(authCompany, {
            flowId: flow._id,
            section: 'overview',
          }),
        );
        return;
      }
      if (flow._id !== 'new') {
        setSelectedFlow(flow._id);
      }
      context.set({
        type: 'flow',
        flow: flow._id,
        flowMode: flow.mode,
        callback: newFlow => {
          context.pop();
          setSelectedFlow(newFlow._id);
        },
      });
    };

  const createPagesFlow = editFlow({ _id: 'new', mode: 'pages' });

  const exportFlow = flow => async () => {
    const { value } = await actions.chatFlows.exportFlow(flow._id);
    const json = JSON.stringify(value);
    const blob = new Blob([json], { type: 'application/json' });
    const href = await URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `${flow.name}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const importFlow = async files => {
    await actions.chatFlows.importFlow(files[0]);
    closeImportModal();
    incNumImports();
  };

  const handleAgentImported = async result => {
    await actions.chatFlows.load();
    incNumImports();
    const newFlowId = result?.chatFlowId;
    if (newFlowId) {
      navigate(
        buildAgenticPath(authCompany, {
          flowId: newFlowId,
          section: 'overview',
        }),
      );
    }
  };

  const deleteFlow = flow => () => {
    setToDelete(flow);
  };

  const confirmDelete = async () => {
    try {
      if (selectedFlow === toDelete._id) {
        setSelectedFlow(null);
      } else if (current?.intent === toDelete._id) {
        pop();
      } else if (current?.skill === toDelete._id) {
        pop();
      } else if (current?.agentTool === toDelete._id) {
        pop();
      }
      if (chatFlows[toDelete._id]) {
        await actions.chatFlows.destroy(toDelete._id, { deleteExclusive });
      } else if (chatFlowIntents[toDelete._id]) {
        await actions.chatFlowIntents.destroy(toDelete._id);
      } else if (chatFlowEntityTypes[toDelete._id]) {
        await actions.chatFlowEntityTypes.destroy(toDelete._id);
      } else if (chatFlowIntegrations[toDelete._id]) {
        await actions.chatFlowIntegrations.destroy(toDelete._id);
      } else if (toDelete._agentSkill) {
        await AgentSkill.destroy(toDelete._id);
      } else if (toDelete._agentTool) {
        await AgentTool.destroy(toDelete._id);
      } else if (selectedFlow && chatFlows[selectedFlow].pages?.find(p => p._id === toDelete._id)) {
        await actions.chatFlowPages.destroy(selectedFlow, toDelete._id);
        await actions.chatFlows.find(selectedFlow);
      } else if (selectedFlow && toDelete.page) {
        await actions.chatFlowPages.removeRoute(selectedFlow, toDelete.page, toDelete._id);
        actions.chatFlows.find(selectedFlow);
      }
    } catch (err) {
      if (err.data?.type === 'UnprocessableEntityError') {
        actions.alert.show({
          label: I18n.t('ChatFlowSettings.Could not delete'),
          message: (
            <Translate
              value="ChatFlowSettings.The element was used in"
              count={err.data.key?.join ? err.data.key.length : 0}
              used={err.data.key?.join ? joinWithFinalAnd(err.data.key, true) : err.data.key}
              dangerousHTML
            />
          ),
          caption: I18n.t('ChatFlowSettings.Please remove all usages'),
          variant: 'error',
          autoHideDuration: ms('15 seconds'),
        });
      }
    }
    setToDelete(null);
    setDeletePreview(null);
    setDeleteExclusive(false);
  };

  const closeCreateSkill = () => {
    setCreateSkillOpen(false);
    setNewSkillForm({ displayName: '', description: '' });
  };

  const closeCreateAgentTool = () => {
    setCreateAgentToolOpen(false);
    setNewAgentToolForm({ name: '', description: '', kind: 'http' });
    setCreateAgentToolError('');
  };

  const handleCreateSkill = async () => {
    const displayName = newSkillForm.displayName.trim();
    const description = newSkillForm.description.trim();
    if (!displayName || !description) return;
    const skill = await AgentSkill.create({
      displayName,
      name: agenticSlug(displayName),
      description,
    });
    closeCreateSkill();
    if (selectedFlow) {
      navigate(
        buildAgenticPath(authCompany, {
          flowId: selectedFlow,
          section: 'skills',
          skillId: skill._id,
        }),
      );
    } else {
      context.set({ type: 'skill', skill: skill._id });
    }
  };

  const handleCreateAgentTool = async () => {
    const rawName = newAgentToolForm.name.trim();
    const description = newAgentToolForm.description.trim();
    if (!rawName || !description) return;
    const name = agenticSlug(rawName);
    setCreateAgentToolError('');
    try {
      const tool = await AgentTool.create({
        kind: newAgentToolForm.kind,
        name,
        description,
        http:
          newAgentToolForm.kind === 'http'
            ? { method: 'POST', urlTemplate: 'https://example.com/webhook', outputMapping: {} }
            : undefined,
        codeKey: newAgentToolForm.kind === 'code' ? name : undefined,
      });
      closeCreateAgentTool();
      if (selectedFlow) {
        navigate(
          buildAgenticPath(authCompany, {
            flowId: selectedFlow,
            section: 'tools',
            toolId: tool._id,
          }),
        );
      } else {
        context.set({ type: 'agent-tool', agentTool: tool._id });
      }
    } catch (err) {
      setCreateAgentToolError(toolNameConflictMessage(err));
    }
  };

  const handleCreateAgent = async form => {
    const result = await actions.chatFlows.create(form);
    await actions.chatFlows.load();
    const created = result?.value || result?.payload || result;
    if (created?._id) {
      setSelectedFlow(created._id);
    }
  };

  const handleOpenCreate = () => {
    if (hasAgentic) {
      setNewAgentOpen(true);
    } else {
      createPagesFlow();
    }
  };

  const chatFlowsList = orderBy(Object.values(chatFlows || {}), [
    i => (i?.name || '').toLowerCase(),
  ]);
  const selectedFlowDoc = selectedFlow ? chatFlows[selectedFlow] : null;

  useEffect(() => {
    if (selectedFlow && chatFlowsLoaded && !selectedFlowDoc) {
      // Flow id in URL no longer exists → drop back to the list.
      navigate(agenticSectionPath(authCompany), { replace: true });
    }
  }, [selectedFlow, chatFlowsLoaded, selectedFlowDoc, navigate, authCompany]);

  // Page / entity-type / intent / integration IDs that point at deleted or
  // unknown entries should fall back to the workspace root for the flow.
  useEffect(() => {
    if (!selectedFlow || !selectedFlowDoc) return;
    let invalid = false;
    if (route.pageId && route.pageId !== 'new') {
      const pages = selectedFlowDoc.pages || [];
      if (!pages.some(p => String(p._id) === route.pageId)) invalid = true;
    }
    if (route.entityTypeId && route.entityTypeId !== 'new') {
      if (!chatFlowEntityTypes?.[route.entityTypeId]) invalid = true;
    }
    if (route.intentId && route.intentId !== 'new') {
      if (!chatFlowIntents?.[route.intentId]) invalid = true;
    }
    if (route.integrationId && route.integrationId !== 'new') {
      if (!chatFlowIntegrations?.[route.integrationId]) invalid = true;
    }
    if (invalid) {
      navigate(buildAgenticPath(authCompany, { flowId: selectedFlow }), {
        replace: true,
      });
    }
  }, [
    selectedFlow,
    selectedFlowDoc,
    route.pageId,
    route.entityTypeId,
    route.intentId,
    route.integrationId,
    chatFlowEntityTypes,
    chatFlowIntents,
    chatFlowIntegrations,
    navigate,
    authCompany,
  ]);

  const contextValue = useMemo(
    () => ({
      context,
      setToDelete,
      selectedFlowId: selectedFlow,
      chatFlows,
      onBackToList,
    }),
    [context, setToDelete, selectedFlow, chatFlows, onBackToList],
  );

  const renderMain = () => {
    if (!chatFlowsLoaded) {
      return (
        <Box sx={styles.loading}>
          <Loading size={48} />
        </Box>
      );
    }

    const cur = context.current;
    const standalonePane = !selectedFlow && cur?.type && STANDALONE_PANE_TYPES.has(cur.type);

    if (standalonePane) {
      return (
        <Box sx={styles.routeShell}>
          <RightPane
            context={context}
            chatFlows={chatFlows}
            chatFlowEntityTypes={chatFlowEntityTypes}
            chatFlowIntents={chatFlowIntents}
            chatFlowIntegrations={chatFlowIntegrations}
            selectedFlow={selectedFlow}
          />
        </Box>
      );
    }

    if (chatFlowsList.length === 0) {
      return (
        <AgentsEmptyState
          hasAgentic={hasAgentic}
          onCreate={handleOpenCreate}
          onImport={openImportModal}
        />
      );
    }

    return (
      <Box sx={styles.routeShell}>
        <Routes>
          <Route
            index
            element={
              <AgentsList
                flows={chatFlowsList}
                hasAgentic={hasAgentic}
                onSelect={setSelectedFlow}
                onCreate={handleOpenCreate}
                onImport={openImportModal}
                onEdit={flow => editFlow(flow)()}
                onExport={flow => exportFlow(flow)()}
                onDelete={flow => deleteFlow(flow)()}
              />
            }
          />
          <Route
            path=":flowId/*"
            element={
              <ChatFlowFlowRoute
                authCompany={authCompany}
                chatFlows={chatFlows}
                chatFlowsLoaded={chatFlowsLoaded}
                chatFlowEntityTypes={chatFlowEntityTypes}
                chatFlowIntents={chatFlowIntents}
                chatFlowIntegrations={chatFlowIntegrations}
                actions={actions}
                onBackToList={onBackToList}
                onCreateSkill={() => setCreateSkillOpen(true)}
                onCreateTool={() => setCreateAgentToolOpen(true)}
                onCreateKnowledge={() => setCreateKnowledgeOpen(true)}
              />
            }
          />
        </Routes>
      </Box>
    );
  };

  return (
    <ChatFlowContext.Provider value={contextValue}>
      <ChatFlowRedirect authCompany={authCompany} />
      <Box sx={styles.root}>{renderMain()}</Box>
      <DeleteDialog
        typeBack={toDelete?.name}
        message={I18n.t('ChatFlowSettings.ConfirmDelete', { name: toDelete?.name })}
        isOpen={toDelete}
        onConfirm={confirmDelete}
        onClose={() => {
          setToDelete(null);
          setDeletePreview(null);
          setDeleteExclusive(false);
        }}
      >
        {agentDeleteTarget && (
          <AgentDeleteExclusivePanel
            preview={deletePreview}
            loading={deletePreviewLoading}
            checked={deleteExclusive}
            onCheckedChange={setDeleteExclusive}
          />
        )}
      </DeleteDialog>
      {hasAgentic ? (
        <ImportAgentModal
          open={importModalOpen}
          onClose={closeImportModal}
          onImported={handleAgentImported}
        />
      ) : (
        <Dialog open={importModalOpen} onClose={closeImportModal}>
          <DialogTitle>{I18n.t('ChatFlowSettings.Import flow')}</DialogTitle>
          <DropzoneBox accept="application/json" onDrop={importFlow} />
        </Dialog>
      )}
      <NewAgentDialog
        open={newAgentOpen}
        onClose={() => setNewAgentOpen(false)}
        onCreate={handleCreateAgent}
        authCompany={authCompany}
      />
      <Dialog open={createSkillOpen} onClose={closeCreateSkill} maxWidth="sm" fullWidth>
        <DialogTitle>{I18n.t('AgenticChatFlow.CreateSkill')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Box>
              <FieldLabel required>{I18n.t('AgenticChatFlow.Name')}</FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder={I18n.t('AgenticChatFlow.SkillNamePlaceholder')}
                value={newSkillForm.displayName}
                onChange={e => setNewSkillForm({ ...newSkillForm, displayName: e.target.value })}
                helperText={
                  newSkillForm.displayName.trim()
                    ? `${I18n.t('AgenticChatFlow.Slug')}: ${agenticSlug(newSkillForm.displayName)}`
                    : undefined
                }
              />
            </Box>
            <Box>
              <FieldLabel required>{I18n.t('AgenticChatFlow.Description')}</FieldLabel>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                placeholder={I18n.t('AgenticChatFlow.SkillDescriptionPlaceholder')}
                value={newSkillForm.description}
                onChange={e => setNewSkillForm({ ...newSkillForm, description: e.target.value })}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mt: 0.75,
                }}
              >
                {I18n.t('AgenticChatFlow.CreateSkillDescriptionHelper')}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreateSkill} color="inherit">
            {I18n.t('Common.Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSkill}
            disabled={!newSkillForm.displayName.trim() || !newSkillForm.description.trim()}
          >
            {I18n.t('Common.Create')}
          </Button>
        </DialogActions>
      </Dialog>
      <CreateKnowledgeDialog
        open={createKnowledgeOpen}
        onClose={() => setCreateKnowledgeOpen(false)}
        onCreated={item => {
          setCreateKnowledgeOpen(false);
          if (selectedFlow) {
            navigate(
              buildAgenticPath(authCompany, {
                flowId: selectedFlow,
                section: 'knowledge',
                knowledgeId: item._id,
              }),
            );
          } else {
            context.set({ type: 'knowledge', knowledge: item._id });
          }
        }}
      />
      <Dialog open={createAgentToolOpen} onClose={closeCreateAgentTool} maxWidth="sm" fullWidth>
        <DialogTitle>{I18n.t('AgenticChatFlow.CreateTool')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Box>
              <FieldLabel required>{I18n.t('AgenticChatFlow.Name')}</FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder={I18n.t('AgenticChatFlow.ToolNamePlaceholder')}
                value={newAgentToolForm.name}
                onChange={e => setNewAgentToolForm({ ...newAgentToolForm, name: e.target.value })}
                helperText={
                  newAgentToolForm.name.trim()
                    ? `${I18n.t('AgenticChatFlow.Slug')}: ${agenticSlug(newAgentToolForm.name)}`
                    : undefined
                }
              />
            </Box>
            <Box>
              <FieldLabel required>{I18n.t('AgenticChatFlow.Description')}</FieldLabel>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                placeholder={I18n.t('AgenticChatFlow.ToolDescriptionPlaceholder')}
                value={newAgentToolForm.description}
                onChange={e =>
                  setNewAgentToolForm({ ...newAgentToolForm, description: e.target.value })
                }
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mt: 0.75,
                }}
              >
                {I18n.t('AgenticChatFlow.CreateToolDescriptionHelper')}
              </Typography>
            </Box>
            {createAgentToolError ? (
              <Typography color="error" variant="body2">
                {createAgentToolError}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreateAgentTool} color="inherit">
            {I18n.t('Common.Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateAgentTool}
            disabled={!newAgentToolForm.name.trim() || !newAgentToolForm.description.trim()}
          >
            {I18n.t('Common.Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </ChatFlowContext.Provider>
  );
};

ChatFlowSettings.propTypes = {
  actions: object.isRequired,
  authCompany: object,
  chatFlows: objectOf(chatFlowShape),
  chatFlowsLoaded: bool,
  chatFlowIntents: objectOf(chatFlowIntentShape),
  chatFlowEntityTypes: objectOf(chatFlowEntityTypeShape),
  chatFlowIntegrations: object,
};

export default connector(ChatFlowSettings);
